<template>
  <v-app>
    <v-main>
      <app-error-messages />
      <v-app-bar
        :height="$vuetify.display.mdAndUp ? 80 : 72"
        class="website-header"
        color="white"
        flat
      >
        <v-container class="website-container website-header__container">
          <div class="website-header__content">
            <img
              alt="Long Live"
              class="website-header__logo"
              src="@/images/logo.svg"
            >
            <nav
              v-if="$vuetify.display.mdAndUp"
              class="website-header__nav"
            >
              <router-link
                v-for="link in links"
                :key="link.text"
                :to="link.to"
                class="website-header__link"
              >
                {{ link.text }}
              </router-link>
            </nav>
          </div>
          <div
            v-if="$vuetify.display.mdAndUp"
            class="website-header__actions"
          >
            <v-btn
              :to="$routeTo(ROUTES.LOGIN)"
              class="text-uppercase"
              color="on-background"
              height="44"
              rounded="lg"
              variant="text"
            >
              {{ $auth.user ? 'Dashboard' : 'Log In' }}
            </v-btn>
            <v-btn-primary
              :to="$routeTo(ROUTES.REGISTER)"
              class="px-6"
              height="47"
            >
              Sign Up
            </v-btn-primary>
          </div>
          <v-btn
            v-else
            aria-label="Open menu"
            icon
            rounded="lg"
            size="40"
            variant="text"
            @click="drawer = !drawer"
          >
            <img
              alt=""
              src="@/images/home/icons/menu-01.svg"
            >
          </v-btn>
        </v-container>
      </v-app-bar>
      <v-navigation-drawer
        v-if="$vuetify.display.smAndDown"
        v-model="drawer"
        location="right"
        width="300"
      >
        <template #append>
          <v-row
            align="start"
            justify="center"
            no-gutters
            class="mb-4"
          >
            <v-col
              cols="6"
            >
              <v-btn-secondary
                :to="$routeTo(ROUTES.LOGIN)"
                block
                border="0"
                variant="text"
              >
                {{ $auth.user ? 'Dashboard' : 'Log In' }}
              </v-btn-secondary>
            </v-col>
            <v-spacer />
            <v-col
              cols="6"
            >
              <v-btn-primary
                :to="$routeTo(ROUTES.REGISTER)"
              >
                Sign Up
              </v-btn-primary>
            </v-col>
          </v-row>
        </template>
        <div class="mt-4">
          <v-btn
            v-for="link in links"
            :key="link.text"
            block
            :active="false"
            :text="link.text"
            :to="link.to"
            variant="text"
          />
        </div>
      </v-navigation-drawer>
      <v-slide-x-reverse-transition mode="out-in">
        <span class="transition-wrapper">
          <slot />
        </span>
      </v-slide-x-reverse-transition>
      <v-footer
        class="website-footer"
        color="primary"
      >
        <v-container class="website-container">
          <div class="website-footer__content">
            <div class="website-footer__copyright">
              © {{ new Date().getFullYear() }} Long Live. All rights reserved.
            </div>
            <nav class="website-footer__links">
              <router-link :to="$routeTo(ROUTES.TERMS_OF_SERVICE)">
                Terms
              </router-link>
              <router-link :to="$routeTo(ROUTES.PRIVACY_POLICY)">
                Privacy
              </router-link>
              <router-link :to="$routeTo(ROUTES.COOKIE_POLICY)">
                Cookies
              </router-link>
            </nav>
          </div>
        </v-container>
      </v-footer>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import {defineComponent} from "vue"
import AppErrorMessages from "@/components/AppErrorMessages.vue"

export default defineComponent({
  components: {AppErrorMessages},
  data() {
    return {
      drawer: false as boolean,
      isDev: import.meta.env.VITE_APP_ENV === "dev" || import.meta.env.VITE_APP_ENV === "local",
      links: [
        {text: 'Home', to: '/#hero'},
        {text: 'How It Works', to: '/#works'},
        {text: 'Pricing', to: '/#pricing'},
        {text: 'Contact', to: '/#contact'},
      ]
    }
  },
})
</script>

<style lang="scss" scoped>
@use "@/scss/website";

.website-header {
  box-shadow: inset 0 -1px 0 #F2F4F7;

  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px 0 25px;

    @media #{website.$md-and-up} {
      padding: 0 32px;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 40px;
  }

  // logo.svg is cropped to the mark, the Figma slot (161x42 desktop, 128x38 mobile) adds 7px of air around it
  &__logo {
    display: block;
    width: 117px;
    margin-left: 6px;

    @media #{website.$md-and-up} {
      width: 147px;
      margin: 0 7px;
    }
  }

  &__nav {
    display: flex;
    align-items: center;
    gap: 32px;
  }

  &__link {
    @include website.sans;
    color: rgb(var(--v-theme-on-background));
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.website-footer {
  padding: 24px 0;

  @media #{website.$md-and-up} {
    padding: 64px 0 48px;
  }

  &__content {
    display: flex;
    flex-direction: column-reverse;
    gap: 16px;
    padding-top: 32px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);

    @media #{website.$md-and-up} {
      flex-direction: row;
      align-items: center;
      gap: 32px;
    }
  }

  &__copyright,
  &__links a {
    @include website.sans;
    color: white;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  &__copyright {
    flex: 1 0 0;
  }

  &__links {
    display: flex;
    gap: 16px;
  }
}
</style>
